// © Microsoft Corporation. All rights reserved.

import { AutoSuggest } from "./AutoSuggest";
import { AutoSuggestDelayBinding, AutoSuggestInitProperties, autoSuggestPerfMarker } from "./AutoSuggest.interfaces";
import { TelemetryDOM } from "@msnews/telemetry-core";
import { TelemetryActionType, TelemetryBehaviorType, TelemetryContentType } from "@msnews/telemetry-contracts";

/**
 * Initialize autosuggest instance
 * @param initProps init props for autosuggest
 */
export function initializeAutoSuggest(initProps: AutoSuggestInitProperties): void {
    if (!initProps || initProps.disableAutoSuggest) {
        return;
    }

    const { autoSuggestProperties, requestId, isDarkMode, searchBoxContext, startPerfMarkerHandler } = initProps;
    const { osMarket, osLanguage, useMarketBasedUrl, delayBind, businessSearchResultsEnabled, cookieSharingDelayInSeconds } = autoSuggestProperties;

    const autoSuggestInstance = new AutoSuggest(
        autoSuggestProperties,
        useMarketBasedUrl && osMarket,
        useMarketBasedUrl && osLanguage,
        requestId,
        isDarkMode);

    if (delayBind === AutoSuggestDelayBinding.OnUserInteraction
        || (delayBind === AutoSuggestDelayBinding.OnBusinessSearchResultsReady && !businessSearchResultsEnabled)) {
        searchBoxContext.autoSuggestEventHandler = autoSuggestHandler.bind(searchBoxContext, autoSuggestInstance);
        searchBoxContext.registerAutoSuggestOnSearchEventHandler = registerAutoSuggestOnSearchEvent.bind(searchBoxContext, initProps);
        const inputRef = getRef(searchBoxContext.inputRef);
        inputRef.addEventListener("keyup", searchBoxContext.autoSuggestEventHandler);
        inputRef.addEventListener("click", searchBoxContext.autoSuggestEventHandler);
    } else if (delayBind === AutoSuggestDelayBinding.Immediately
        || delayBind === AutoSuggestDelayBinding.OnPageLoad
        || delayBind === AutoSuggestDelayBinding.OnBusinessSearchResultsReady) {
        // This is a custom perf marker which should be added just before we start downloading autosuggest script
        if (startPerfMarkerHandler) {
            startPerfMarkerHandler(autoSuggestPerfMarker);
        }
        searchBoxContext.registerAutoSuggestOnSearchEventHandler = registerAutoSuggestOnSearchEvent.bind(searchBoxContext, initProps);
        autoSuggestInstance.addAutoSuggest(searchBoxContext.registerAutoSuggestOnSearchEventHandler);
    }

    if (cookieSharingDelayInSeconds === 1) {
        autoSuggestInstance.invokeCookieSharing();
    }
}

/**
 * Handles mouse and keyboard events on the autosuggest instance
 * @param autoSuggestInstance autosuggest instance
 * @param e mouse event
 */
export function autoSuggestHandler(autoSuggestInstance: AutoSuggest, e: React.MouseEvent<HTMLInputElement> | React.KeyboardEvent<HTMLInputElement>): void {
    if (e.type === "keyup") {
        const key = (e as React.KeyboardEvent).key;
        if (key === "Tab" || key === "Escape") {
            // skip adding autosuggest for tab and escape key press
            return;
        }
    }

    const isAutoSuggestAdded = autoSuggestInstance.addAutoSuggest(this.registerAutoSuggestOnSearchEventHandler);

    // remove handlers to avoid adding autosuggest on every click/keypress
    if (isAutoSuggestAdded) {
        const inputRef = getRef(this.inputRef);
        inputRef.removeEventListener("click", this.autoSuggestEventHandler);
        inputRef.removeEventListener("keyup", this.autoSuggestEventHandler);
    }
}

/**
 * Registers autosuggest on search event
 * @param initProps autosuggest init props
 */
export function registerAutoSuggestOnSearchEvent(initProps: AutoSuggestInitProperties): void {
    const { autoSuggestProperties, endPerfMarkerHandler, searchBoxContext, searchOpenInNewWindow, action, telemetryCallback } = initProps;
    const { delayBind } = autoSuggestProperties;

    if (delayBind === AutoSuggestDelayBinding.OnUserInteraction
        || delayBind === AutoSuggestDelayBinding.OnPageLoad
        || delayBind === AutoSuggestDelayBinding.OnBusinessSearchResultsReady) {
        // add perf end marker
        if (endPerfMarkerHandler) {
            endPerfMarkerHandler(autoSuggestPerfMarker);
        }
    }

    if (searchOpenInNewWindow) {
        // if search opens in new window skip setup to trigger tracking call before page unload
        return;
    }

    // in case of the search results being opened in SAME tab, the autoSuggest js gets unloaded before the 'ASSugClicked' event gets fired due to search result navigation
    // to fire the tracking beacon, we instead listen to 'onSearch' event which is fired just before the form submit action
    // in case of search results being opened in NEW tab, we continue to listen to 'ASSugClicked' event
    // sj_evt is AutoSuggest Events object name
    const eventObject = window["sj_evt"];
    const prevFireEvt = eventObject && eventObject.fire;

    // add a listener to bing auto suggest 'onSearch' event
    if (prevFireEvt) {
        eventObject.fire = function (...args: unknown[]): void {
            const [eventType, target] = args || [];

            // Set the telemetry content type accordingly
            if (eventType === "AS.Navigate") {
                const formRef = getRef(searchBoxContext.formRef);
                if (formRef && formRef.attributes && formRef.attributes["data-t"]) {
                    const contentItem = TelemetryDOM.get(formRef);
                    contentItem.contract.content.type = getTelemetryContentType(target["stype"]);
                    TelemetryDOM.setAttribute(formRef, contentItem.contract);
                }
            }

            if (eventType === "onSearch") {
                searchBoxContext.onAutoSuggestSuggestionClickHandler = onAutoSuggestSuggestionClick.bind(searchBoxContext, action, telemetryCallback);
                searchBoxContext.onAutoSuggestSuggestionClickHandler();
            }

            prevFireEvt.apply(searchBoxContext, args);
        };
    }
}

/**
 * Handler when autosuggest suggestion is selected
 * @param action search action url
 * @param telemetryCallback telemetry callback
 */
export function onAutoSuggestSuggestionClick(action: string, telemetryCallback: (element: HTMLElement, action?: string, behavior?: number) => void): void {
    // attr(searchForm, "action") will sometimes be a canonical url for vertical page, but searchForm.Action will be a full URL.
    // So check if searchForm.Action has "://". If it does, which means it is a full URL, then just use it as destination
    let dest = action;
    const formRef = getRef(this.formRef);
    if (dest) {
        const searchTypeAutoSuggest = (dest.indexOf("?") !== -1 ? "&" : "?") + "msnst=AS";
        dest = dest + searchTypeAutoSuggest;

        // Updates the destinationUrl on telemetry contract before firing the event
        if (formRef.attributes && formRef.attributes["data-t"]) {
            // const itemId = JSON.parse(formRef.attributes["data-t"].value).i;
            const contentItem = TelemetryDOM.get(formRef);
            contentItem.contract.destinationUrl = dest;
            TelemetryDOM.setAttribute(formRef, contentItem.contract);
        }
    }

    telemetryCallback(formRef, TelemetryActionType.Submit, TelemetryBehaviorType.TextSearch);
}

/**
 * Returns the proper reference to the html element
 * @param ref React Ref object or HTML element
 */
function getRef(ref) {
    return ref && ref.current ? ref.current : ref;
}

/**
 * Returns the appropriate telemetry content type value
 * @param stype stype attribute value
 */
function getTelemetryContentType(stype: string) {
    if (!stype) {
        return TelemetryContentType.Undefined;
    }

    const lowercaseLabel = stype.toLowerCase();
    if (lowercaseLabel === "hs") {
        return TelemetryContentType.SearchHistory;
    }

    if (lowercaseLabel === "pn") {
        return TelemetryContentType.TrendingSearch;
    }

    return TelemetryContentType.AutoSuggestSearch;
}
